import DarkButton from "components/actions/DarkButton";
import { useEffect, useState } from "react";
import InputField from "components/fields/InputField";
import DatePicker from "components/fields/DatePicker";
import Select from "components/fields/Select";
import GrayButton from "components/actions/GrayButton";
import AtriumApi from "api";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import MapboxAddressInput from "components/mapbox";

const ManageStaffForm = (props) => {
  const { onClose, getStaffList } = props;

  //   const auth = useAuth();

  const [user, setUser] = useState(
    {
      status: props?.user?.status || "",
      campus: props?.user?.campus || "",
      firstName: props?.user?.firstName || "",
      lastName: props?.user?.lastName || "",
      email: props?.user?.email || "",
      phone: props?.user?.phone || "",
      dob: props?.user?.dob || "",
      familyRelationship: props?.user?.familyRelationship || "",
      gender: props?.user?.gender || "",
      avatar: props?.user?.avatar?.url || "",
      address: props?.user?.address || "",
      city: props?.user?.city || "",
      street: props?.user?.street || "",
      state: props?.user?.state || "",
      zip: props?.user?.zip || "",
      country: props?.user?.country || "",
      id: props?.user?.id || "",
    } || {}
  );
  const [errors, setErrors] = useState([]);
  const [image, setImage] = useState(props.user?.avatar?.url || "");
  const [imageFile, setImageFile] = useState(null); // <-- Track the actual File

  const [location, setLocation] = useState(
    {
      type: "Point",
      coordinates: [props?.user?.lng, props?.user?.lat],
    } || {}
  );

  const [campuses, setCampuses] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImage(imageUrl);
      setImageFile(file); // <-- Store the file
    }
  };

  useEffect(() => {
    const fetchAllCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        if (response) {
          setCampuses(response?.data?.data?.campuses?.data);
        }
      } catch (error) {
        console.log("fetching all campuses error", error);
      }
    };
    fetchAllCampuses();
  }, []);

  useEffect(() => {
    if (!user.id && props.user?.id) {
      setUser({
        status: props?.user?.status,
        campus: props?.user?.campus,
        firstName: props?.user?.firstName,
        lastName: props?.user?.lastName,
        email: props?.user?.email,
        phone: props?.user?.phone,
        dob: props?.user?.dob,
        familyRelationship: props?.user?.familyRelationship,
        gender: props?.user?.gender,
        avatar: props?.user?.avatar?.url,
        address: props?.user?.address,
        city: props?.user?.city,
        state: props?.user?.state,
        street: props?.user?.street,
        zip: props?.user?.zip,
        country: props?.user?.country,
        id: props?.user?.id,
      });
      setImage(props?.user?.avatar?.url);
      setLocation({
        type: "Point",
        coordinates: [props?.user?.lng, props?.user?.lat],
      });
    }
  }, [user, props.user]);

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Status is required"),
    campus: Yup.string().required("Campus is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
      .required("Phone number is required"),
    familyRelationship: Yup.string().required(
      "Family relationship is required"
    ),
    dob: Yup.string().required("Date of birth is required"),
    gender: Yup.string().required("Gender is required"),
    address: Yup.string().required("Address is required"),
  });

  const onSubmit = (values) => {
    setIsLoading(true);
    const formElement = document.querySelector("form");
    formElement.classList.add("animate-pulse");

    const submitButton = formElement.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");

    const formData = new FormData();

    // Always hardcode the role as 'staff'
    formData.append("role", "staff");

    // Add other user fields to FormData, skipping 'avatar' and 'role'
    Object.keys(values).forEach((key) => {
      if (key !== "avatar" && key !== "role") {
        formData.append(key, values[key]);
      }
    });

    formData.append("location", JSON.stringify(location));

    // Add avatar based on whether a new file is selected
    if (imageFile) {
      formData.append("avatar", imageFile);
    } else {
      formData.append("avatar", image);
    }

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    const request = user?.id
      ? AtriumApi.put(`/v1/users/${user?.id}`, formData, config)
      : AtriumApi.post("/v1/users", formData, config);

    request
      .then((response) => {
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        if (response.status === 422) {
          toast.error(response.data.message);
        } else if (response.status === 200) {
          toast.success(
            `Staff ${user.id ? "updated" : "created"} successfully.`
          );
          onClose();
          getStaffList();
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        toast.error(error?.data?.message || "Something went wrong");
        formElement.classList.remove("animate-pulse");
        submitButton.removeAttribute("disabled");
        setErrors([
          { path: "general", message: "An error occurred while saving" },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // date format as per mac book as well. MM/DD/YYYY
  function formatToMMDDYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={user || {}}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault(); // Stop default form behavior
              handleSubmit(); // Call Formik's submit
            }}
            noValidate
          >
            <p className="font-bold text-xl text-[#1B2559] dark:text-white">
              Settings
            </p>
            <div className="flex gap-4 pt-4">
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="status"
                >
                  Status
                </label>
                <Select
                  name="status"
                  value={values.status || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{
                    borderColor: touched.status && errors.status ? "red" : "",
                  }}
                  required
                >
                  <option value="" disabled hidden>
                    Select status
                  </option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
                {touched.status && errors.status && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.status}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col pt-4">
              <label
                className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                htmlFor="campus"
              >
                Campus
              </label>
              <Select
                name="campus"
                value={values.campus || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                style={{
                  borderColor: touched.campus && errors.campus ? "red" : "",
                }}
              >
                <option value="" disabled hidden>
                  Select a campus
                </option>
                {campuses?.length > 0 ? (
                  campuses.map((campus) => (
                    <option key={campus?.id} value={campus?.id}>
                      {campus?.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No campuses found
                  </option>
                )}
              </Select>
              {touched.campus && errors.campus && (
                <p className="text-red-500 text-xs mt-1 ml-2">
                  {errors.campus}
                </p>
              )}
            </div>

            <p className="font-bold text-xl text-[#1B2559] dark:text-white py-4">
              User details
            </p>
            <div className="flex items-center space-x-4 mb-3">
              {/* Profile Picture */}
              {image ? (
                <img
                  src={image}
                  alt="Profile"
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="h-[64px] w-[64px] rounded-full bg-gray-300 flex items-center justify-center text-sm text-gray-500">
                  No Image
                </div>
              )}

              {/* Upload Button */}
              <div>
                {/* <DarkButton
              size="sm"
              htmlFor="upload"
            >
              Upload picture
            </DarkButton> */}
                <label
                  htmlFor="upload"
                  className="cursor-pointer text-[12px] px-3 py-2 text-white bg-blue-800 rounded-xl hover:bg-blue-800 transition"
                >
                  Upload picture
                </label>
                <input
                  id="upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              {/* First Name Field */}
              <div className="flex-1 flex flex-col">
                <InputField
                  variant="user_form"
                  label="First name"
                  placeholder=""
                  name="firstName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.firstName}
                  touched={touched.firstName}
                />
                {/* {touched.firstName &&
                  errors.find((e) => e.path === "firstName") && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.find((e) => e.path === "firstName").message}
                    </p>
                  )} */}
              </div>
              <div className="flex-1 flex flex-col">
                <InputField
                  variant="user_form"
                  label="Last name"
                  placeholder=""
                  name="lastName"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.lastName}
                  touched={touched.lastName}
                />
                {/* {touched.lastName &&
                  errors.find((e) => e.path === "lastName") && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.find((e) => e.path === "lastName").message}
                    </p>
                  )} */}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              {/* Email Field */}
              <div className="flex-1 flex flex-col">
                <InputField
                  variant="user_form"
                  label="Email Address"
                  placeholder=""
                  name="email"
                  type="email"
                  extra="flex-1 flex flex-col"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email}
                  touched={touched.email}
                />
                {/* {touched.email && errors.find((e) => e.path === "email") && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.find((e) => e.path === "email").message}
                  </p>
                )} */}
              </div>
              <div className="flex-1 flex flex-col">
                <InputField
                  variant="user_form"
                  label="Contact phone"
                  placeholder=""
                  name="phone"
                  type="text"
                  extra="flex-1 flex flex-col"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  touched={touched.phone}
                />
                {/* {touched.phone && errors.find((e) => e.path === "phone") && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.find((e) => e.path === "phone").message}
                  </p>
                )} */}
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <div className="flex-1 flex flex-col">
                {/* <DatePicker
                  isFutureDateDisabled
                  name="dob"
                  label="Date of birth"
                  height={30}
                  state={
                    !values.dob && touched.dob && errors.dob !== -1
                      ? "error"
                      : ""
                  }
                  value={
                    (values.dob &&
                      new Date(values.dob).toLocaleDateString(["en-US"])) ||
                    ""
                  }
                  onChange={(v) =>
                    setFieldValue("dob", v.toLocaleDateString(["en-US"]))
                  }
                  onBlur={handleBlur}
                /> */}
                <DatePicker
                  isFutureDateDisabled
                  name="dob"
                  label="Date of birth"
                  height={30}
                  state={
                    !values.dob && touched.dob && errors.dob !== -1
                      ? "error"
                      : ""
                  }
                  value={formatToMMDDYYYY(values.dob)}
                  onChange={(v) => setFieldValue("dob", formatToMMDDYYYY(v))}
                  onBlur={handleBlur}
                />

                {touched.dob && errors.dob && (
                  <p className="text-red-500 text-xs mt-1 ml-2">{errors.dob}</p>
                )}
              </div>
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="family-relationship"
                >
                  Family relationship
                </label>
                <Select
                  name="familyRelationship"
                  value={values.familyRelationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.familyRelationship}
                  touched={touched.familyRelationship}
                  style={{
                    borderColor:
                      touched.familyRelationship && errors.familyRelationship
                        ? "red"
                        : "",
                  }}
                >
                  <option value="" disabled hidden></option>
                  <option value="primary">Primary</option>
                  <option value="married">Married</option>
                  <option value="child">Child</option>
                </Select>
                {touched.familyRelationship && errors.familyRelationship && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.familyRelationship}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <div className="flex-1 flex flex-col">
                <label
                  className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                  htmlFor="gender"
                >
                  Gender
                </label>
                <Select
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.gender}
                  touched={touched.gender}
                  style={{
                    borderColor: touched.gender && errors.gender ? "red" : "",
                  }}
                  required
                >
                  <option value="" disabled hidden>
                    Select gender
                  </option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  {/* <option value="other">Non-binary</option>
            <option value="na">Prefer not to tell</option> */}
                </Select>
                {touched.gender && errors.gender && (
                  <p className="text-red-500 text-xs mt-1 ml-2">
                    {errors.gender}
                  </p>
                )}
              </div>
              <div className="flex-1"></div>
            </div>

            <p className="font-bold text-xl text-[#1B2559] dark:text-white pt-5">
              Location
            </p>
            <div>
              <MapboxAddressInput
                label="Address"
                id="address"
                name="address"
                value={values?.address || props.user?.address || ""}
                onResult={(data) => {
                  setLocation({
                    type: "Point",
                    coordinates: [data?.lng, data?.lat],
                  });

                  if (data) {
                    setFieldValue("address", data?.address || "");
                    setFieldValue("state", data?.state || "");
                    setFieldValue("city", data?.city || "");
                    setFieldValue(
                      "street",
                      data?.street || data?.address || ""
                    );
                    setFieldValue("zip", data?.zipCode || "");
                  }
                }}
                onClearInput={() => {
                  // Clear all related address fields
                  setLocation({}); // or set it to default if needed
                  setFieldValue("address", "");
                  setFieldValue("state", "");
                  setFieldValue("city", "");
                  setFieldValue("street", "");
                  setFieldValue("zip", "");
                }}
              />
              {!values.address && touched.address && errors.address && (
                <p className="text-red-500 text-xs mt-1 ml-2">
                  {errors.address}
                </p>
              )}
            </div>
            <div className="flex gap-4 pt-4">
              <InputField
                variant="user_form"
                label="State"
                placeholder=""
                name="state"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.state}
                touched={touched.state}
              />
              <InputField
                variant="user_form"
                label="City"
                placeholder=""
                name="city"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.city}
                touched={touched.city}
              />
            </div>
            <div className="flex gap-4 pt-4">
              <InputField
                variant="user_form"
                label="Street address"
                placeholder=""
                name="street"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.street}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.street}
                touched={touched.street}
              />
              <InputField
                variant="user_form"
                label="Zip code"
                placeholder=""
                name="zip"
                type="text"
                extra="flex-1 flex flex-col"
                value={values.zip}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.zip}
                touched={touched.zip}
              />
            </div>

            <div className="flex gap-4 py-6">
              <DarkButton size={"lg"} type="submit" disabled={isLoading}>
                {user?.id ? "Save changes" : "Create staff"}
              </DarkButton>

              <GrayButton
                size={"lg"}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                Cancel
              </GrayButton>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ManageStaffForm;
